// ============================================================
// TMR Portal Admin — sincronia da Empresa com o legado: estado e reprocessamento
// ============================================================
import { ApiError } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useOnboardingStatus, useRetryExport } from '../../data/companies/queries';
import { syncState } from '../../data/companies/status';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';

/**
 * The icon that goes with each state — clock for running, alert for stuck, and a direction for the two that
 * are in step: an Empresa that came from the legacy did not travel the same way as one pushed to it.
 */
const STATUS_ICON = {
  Pending: Icons.clock,
  Exported: Icons.check,
  Imported: Icons.download,
  Failed: Icons.alert,
} as const;

/** Props for {@link OnboardingStatusPanel}. */
interface OnboardingStatusPanelProps {
  /** The Empresa being replicated. */
  companyId: string;
}

/**
 * How the Empresa stands between tmr_v2 and the legacy system, and the way out of a failure.
 *
 * This is the portal's single vocabulary for a backend operation that finishes later — pending, done,
 * failed with a reason, reprocess (skill `frontend-architecture`, Regra 7). Contracts and rates reuse it
 * rather than inventing their own.
 *
 * It stays a callout rather than the header badge: the header carries the **business** status, which is what
 * the operator works against. This says whether the two systems agree, which matters only when they do not.
 */
export function OnboardingStatusPanel({ companyId }: OnboardingStatusPanelProps) {
  const status = useOnboardingStatus(companyId);
  const retry = useRetryExport(companyId);
  const canRetry = usePermission(PERMISSIONS.companyOnboard);

  if (status.isPending || status.isError) return null;

  const { status: exportStatus, recordOrigin, failureReason, legacyProducerId } = status.data;
  const sync = syncState(exportStatus, recordOrigin);

  const tone =
    exportStatus === 'Failed'
      ? { bg: 'oklch(96% 0.03 28)', border: 'oklch(80% 0.09 28)' }
      : { bg: 'var(--cream-100)', border: 'var(--border-default)' };

  return (
    <div
      style={{
        padding: '14px 16px',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 'var(--radius-sm)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--fg-muted)', display: 'flex', paddingTop: 2 }}>
          <StrokeIcon d={STATUS_ICON[sync]} size={17} />
        </span>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-900)' }}>Sincronia com o sistema legado</span>
            <StatusBadge status={sync} />
          </div>
          <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--fg-muted)', maxWidth: 620, lineHeight: 1.5 }}>
            {sync === 'Pending' && 'Em andamento. Esta tela se atualiza sozinha.'}
            {sync === 'Imported' && (
              <>
                Importada do sistema legado
                {legacyProducerId != null && (
                  <> — produtor <span style={{ fontFamily: 'var(--font-mono)' }}>#{legacyProducerId}</span> de lá</>
                )}
                .
              </>
            )}
            {sync === 'Exported' && (
              <>
                Exportada para o sistema legado
                {legacyProducerId != null && (
                  <> — produtor <span style={{ fontFamily: 'var(--font-mono)' }}>#{legacyProducerId}</span> lá</>
                )}
                .
              </>
            )}
            {sync === 'Failed' && (failureReason || 'A replicação falhou sem um motivo registrado.')}
          </p>
          {retry.isError && (
            <p role="alert" style={{ margin: '6px 0 0', fontSize: 12.5, color: 'oklch(38% 0.12 28)' }}>
              {retry.error instanceof ApiError ? retry.error.detail : 'Não foi possível reprocessar agora.'}
            </p>
          )}
        </div>
      </div>

      {exportStatus === 'Failed' && canRetry && (
        <Btn
          variant="secondary"
          icon={retry.isPending ? undefined : Icons.swap}
          onClick={retry.isPending ? undefined : () => retry.mutate()}
          disabled={retry.isPending}
        >
          {retry.isPending ? 'Reprocessando…' : 'Reprocessar'}
        </Btn>
      )}
    </div>
  );
}
